import { useState } from 'react'
import { BellRing, Send } from 'lucide-react'
import { ApiError, Button } from '../../core/index.js'
import type { PeriodWorkerReadiness, RemindAllResult } from '../types.js'
import { remindedRecently } from './monthReadinessData.js'

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`
}

/** "Sent to 5 · 2 reminded within the day already · 1 could not be sent". */
export function describeRemindAll(result: RemindAllResult): string {
  const parts = [
    result.sent.length > 0 ? `Sent to ${plural(result.sent.length, 'worker')}` : 'Nobody new to remind',
    result.remindedRecently.length > 0 ? `${result.remindedRecently.length} reminded within the day already` : null,
    result.failed.length > 0 ? `${result.failed.length} could not be sent - try again` : null,
  ]
  return parts.filter((part): part is string => part !== null).join(' · ')
}

/**
 * Remind all: one press asks before it sends, since it messages many workers; then sends a reminder to everyone the
 * To remind filter lists who was not reminded within the day, and says how it went. Hidden when there is nobody to send to.
 */
export function RemindAllButton({ workers, onRemindAll, stacked = false }: { workers: readonly PeriodWorkerReadiness[]; onRemindAll: () => Promise<RemindAllResult>; /** Laid out down a column (the table's Remind heading, above the rows' own Remind buttons) rather than along a line. */ stacked?: boolean }) {
  const [confirming, setConfirming] = useState(false)
  const [pending, setPending] = useState(false)
  const [outcome, setOutcome] = useState<{ text: string; failed: boolean } | null>(null)
  const dueWorkers = workers.filter((worker) => worker.reminder !== null && !remindedRecently(worker.remindedAt, new Date()))
  const due = dueWorkers.length
  const asking = dueWorkers.every((worker) => worker.reminder === 'save_preferences')

  async function send(): Promise<void> {
    setPending(true)
    try {
      const result = await onRemindAll()
      setOutcome({ text: describeRemindAll(result), failed: result.failed.length > 0 })
    } catch (failure) {
      setOutcome({ text: failure instanceof ApiError ? failure.message : 'The reminders could not be sent. Try again.', failed: true })
    } finally {
      setPending(false)
      setConfirming(false)
    }
  }

  return (
    <div className={stacked ? 'flex flex-col items-start gap-1 text-[11px] font-normal normal-case tracking-normal' : 'flex flex-wrap items-center gap-2'}>
      {due > 0 &&
        (confirming ? (
          <>
            <span className="text-[11px] font-bold text-ink">Send {plural(due, 'reminder')} {asking ? 'to save preferences' : 'about missing preferences'}?</span>
            <Button icon={Send} size="sm" disabled={pending} onClick={() => void send()}>
              {pending ? 'Sending…' : `Send ${due}`}
            </Button>
            <button type="button" disabled={pending} onClick={() => setConfirming(false)} className="text-[11px] font-bold text-marine hover:text-marine-dk">
              Cancel
            </button>
          </>
        ) : (
          <Button
            icon={BellRing}
            size="sm"
            variant="secondary"
            onClick={() => {
              setOutcome(null)
              setConfirming(true)
            }}
          >
            Remind all ({due})
          </Button>
        ))}
      {outcome && (
        <span role="status" className={`text-[11px] ${outcome.failed ? 'font-bold text-coral' : 'text-ink-mid'}`}>
          {outcome.text}
        </span>
      )}
    </div>
  )
}
